import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  convertBytesToKilobytesOrMegabytes,
  lastDotTextRemove,
  truncateString,
} from '../../../core/utils/forms-utils';
import { NgTemplateOutlet, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'irembogov-agent-file-upload-input',
  templateUrl: './agent-file-upload-input.component.html',
  styleUrls: ['./agent-file-upload-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AgentFileUploadInputComponent),
      multi: true,
    },
  ],
  imports: [
    NgTemplateOutlet,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class AgentFileUploadInputComponent implements ControlValueAccessor {
  @Input() labelDescription = 'Upload File';
  @Input() minimumFileByteSize = 0;
  @Input() maximumFileByteSize = 5000000;
  @Input() allowedFileTypes: string[] = ['pdf', 'doc', 'docx'];
  @Input() placeholder = 'Upload file';
  @Input() inputId = 'fileupload';
  @Input() displayAsButton = false;
  @Input() required = true;
  @Input() disabled = false;
  @Input() displaySelectedFileName = true;
  @Input()
  fileRequiredErrorMessage = 'File is required';
  @Input()
  fileTypeErrorMessage = `Document must one of these formats <em>${this.allowedFileTypes.join(
    ', '
  )}</em>`;
  @Input()
  fileMinSizeErrorMessage = `File is below the minimum required size of <em>${convertBytesToKilobytesOrMegabytes(
    this.minimumFileByteSize
  )}</em>`;
  @Input()
  fileMaxSizeErrorMessage = `File exceeds the maximum required size of <em>${convertBytesToKilobytesOrMegabytes(
    this.maximumFileByteSize
  )}</em>`;
  @Output() fileUploadEvent = new EventEmitter<File | null>();

  file!: File | null;
  fileControl: FormControl = new FormControl(null);
  fileName = '';
  fileSize = '';
  isFileRequiredValid?: boolean;
  isFileTypeValid?: boolean;
  isFileMaxSizeValid?: boolean;
  isFileMinSizeValid?: boolean;

  /* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function*/
  private _onChange = (value: unknown) => {};
  private onTouch = (value: unknown) => {};
  /* eslint-enable */
  registerOnChange(fn: (_: unknown) => void): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: (_: unknown) => void): void {
    this.onTouch = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  writeValue(value: any) {}

  validate(formControl: FormControl): ValidationErrors | null {
    const validationErrors: ValidationErrors = {};

    if (this.required && !this.file) {
      validationErrors['required'] = true;
    }

    return Object.keys(validationErrors).length ? validationErrors : null;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  getFileControlErrorMessage(): string | undefined {
    if (!this.isFileRequiredValid) {
      return this.fileRequiredErrorMessage;
    }

    if (!this.isFileTypeValid) {
      return this.fileTypeErrorMessage;
    }

    if (!this.isFileMaxSizeValid) {
      return this.fileMaxSizeErrorMessage;
    }

    if (!this.isFileMinSizeValid) {
      return this.fileMinSizeErrorMessage;
    }
    return undefined;
  }

  fileUpload(event: Event) {
    this.file = null;
    this.isFileRequiredValid = true;
    this.isFileMinSizeValid = true;
    this.isFileMaxSizeValid = true;
    this.isFileTypeValid = true;
    const fileList: FileList | null = (event.target as HTMLInputElement).files;
    if (!(fileList && fileList.length > 0)) {
      this._onChange(null);
      this.onTouch(null);
      this.isFileRequiredValid = !this.required;
      this.fileUploadEvent.emit(null);
      return;
    }

    const file: File = fileList[0];
    const fileType = file?.name?.split('.')?.pop();
    const fileName = lastDotTextRemove(file.name);
    this.fileName = `${truncateString(fileName, 35, '---')}.${fileType}`;
    this.fileSize = convertBytesToKilobytesOrMegabytes(file.size);

    if (
      file &&
      fileType &&
      this.allowedFileTypes.length > 0 &&
      this.allowedFileTypes.indexOf(fileType) < 0
    ) {
      this.isFileTypeValid = false;
      this._onChange(null);
      this.onTouch(null);
      this.fileUploadEvent.emit(null);
      return;
    }

    if (this.minimumFileByteSize && file.size < this.minimumFileByteSize) {
      this.isFileMinSizeValid = false;
      this._onChange(null);
      this.onTouch(null);
      this.fileUploadEvent.emit(null);
      return;
    }

    if (file.size > this.maximumFileByteSize) {
      this.isFileMaxSizeValid = false;
      this._onChange(null);
      this.onTouch(null);
      this.fileUploadEvent.emit(null);
      return;
    }

    this._onChange(file);
    this.onTouch(file);
    this.fileUploadEvent.emit(file);
  }

  resetFileInput() {
    console.log('Reset File');
    this.fileName = '';
    this.fileSize = '';
    this.fileControl.reset();
  }
}
